/**
 * SceneManager - Builds the 3D scene: field, stadium, lighting, skybox, camera.
 * Returns references so the Engine can wire entities into the scene.
 * 
 * ULTRA REALISTIC RENDERING:
 * - HDR Environment with IBL
 * - Cascaded Shadow Maps (CSM)
 * - Screen Space Reflections (SSR)
 * - SSAO (Screen Space Ambient Occlusion)
 * - Advanced post-processing pipeline
 */

import * as BABYLON from "babylonjs";
import "babylonjs-loaders";
import { RoundedStadium } from "./RoundedStadium";


// Field dimensions
export const FIELD_LENGTH = 60; // Z-axis
export const FIELD_WIDTH = 40; // X-axis
const WALL_HEIGHT = 3;
const WALL_THICKNESS = 2;

export interface SceneAssets {
  scene: BABYLON.Scene;
  camera: BABYLON.FollowCamera;
  shadowGenerator: BABYLON.ShadowGenerator;
}

export class SceneManager {
  static create(engine: BABYLON.Engine, canvas: HTMLCanvasElement): SceneAssets {
    const scene = new BABYLON.Scene(engine);
    scene.clearColor = new BABYLON.Color4(0.53, 0.81, 0.92, 1); // Light sky blue

    scene.blockMaterialDirtyMechanism = false;

    // --- SKY ---
    SceneManager._createSkybox(scene);

    // --- Camera (Cinematic broadcast style) ---
    const camera = new BABYLON.FollowCamera(
      "followCam",
      new BABYLON.Vector3(0, 16, -22),
      scene
    );
    camera.radius = 16;
    camera.heightOffset = 11;
    camera.rotationOffset = 180;
    camera.cameraAcceleration = 0.05;  // More responsive tracking
    camera.maxCameraSpeed = 30;
    camera.lowerRadiusLimit = 2;       // Prevent going through player
    camera.upperRadiusLimit = 60;      // Prevent going too far away

    // --- CINEMATIC LIGHTING SYSTEM ---

    // Ambient hemisphere for fill
    const hemi = new BABYLON.HemisphericLight(
      "hemi",
      new BABYLON.Vector3(0, 1, 0),
      scene
    );
    hemi.intensity = 0.4; // Boosted fill light
    hemi.groundColor = new BABYLON.Color3(0.05, 0.1, 0.05);
    hemi.diffuse = new BABYLON.Color3(0.5, 0.6, 0.8);

    // Main stadium floodlight - bright, directional
    const sun = new BABYLON.DirectionalLight(
      "sun",
      new BABYLON.Vector3(-0.3, -1, -0.2).normalize(),
      scene
    );
    sun.position = new BABYLON.Vector3(50, 80, 50);
    sun.intensity = 1.8; // Lowered from 3.5 to prevent washing out colors
    sun.diffuse = new BABYLON.Color3(1, 1, 0.95);
    sun.specular = new BABYLON.Color3(1, 1, 1);
    sun.shadowMinZ = 1;
    sun.shadowMaxZ = 150;

    // Point lights for stadium floodlights - stadium model includes its own lights
    // SceneManager._createStadiumLights(scene);

    // --- OPTIMIZED SHADOWS ---
    const shadowGen = new BABYLON.ShadowGenerator(1024, sun); // Further reduced for performance
    shadowGen.bias = 0.001;
    shadowGen.normalBias = 0.02;
    shadowGen.usePercentageCloserFiltering = true; // PCF is usually faster and looks good
    shadowGen.filteringQuality = BABYLON.ShadowGenerator.QUALITY_LOW;
    shadowGen.darkness = 0.3;
    shadowGen.transparencyShadow = false; // Performance boost
    sun.autoCalcShadowZBounds = false; // Prevent expensive re-calc on rotation
    sun.shadowMinZ = 1;
    sun.shadowMaxZ = 150;

    // --- PHOTOREALISTIC GRASS ---
    // Use a Box instead of Ground to give the field thickness and prevent physics tunneling
    const ground = BABYLON.MeshBuilder.CreateBox(
      "field",
      { width: FIELD_WIDTH, height: 1, depth: FIELD_LENGTH },
      scene
    );
    ground.position.y = -0.5; // Top surface at Y=0

    // Create realistic grass material with PBR
    const grassMat = SceneManager._createRealisticGrassMaterial(scene);
    ground.material = grassMat;
    ground.receiveShadows = true;
    ground.freezeWorldMatrix();

    // Field lines
    SceneManager._createFieldLines(scene);

    // --- STADIUM ENVIRONMENT ---
    // Gray walls removed - using stadium model instead
    // SceneManager._createWalls(scene);
    // SceneManager._createStadium(scene, shadowGen);
    RoundedStadium.create(scene);

    // --- ATMOSPHERIC EFFECTS ---
    // Fog disabled as it might be causing visibility issues
    scene.fogMode = BABYLON.Scene.FOGMODE_NONE;

    // --- ULTRA REALISTIC POST-PROCESSING ---
    SceneManager._setupUltraRealisticPostProcessing(scene, camera, engine);

    return { scene, camera, shadowGenerator: shadowGen };
  }

  /**
   * Create realistic grass material with procedural striped texture
   */
  private static _createRealisticGrassMaterial(scene: BABYLON.Scene): BABYLON.StandardMaterial {
    const grassMat = new BABYLON.StandardMaterial("grassMat", scene);

    const textureSize = 1024;
    const albedoTexture = new BABYLON.DynamicTexture("grassAlbedo", { width: textureSize, height: textureSize }, scene);
    const ctxAlb = albedoTexture.getContext();
    const stripHeight = textureSize / 16;

    for (let y = 0; y < textureSize; y++) {
      const stripIndex = Math.floor(y / stripHeight);
      const isDarkStrip = stripIndex % 2 === 1;

      for (let x = 0; x < textureSize; x++) {
        let r, g, b;
        if (isDarkStrip) {
          r = 30; g = 100; b = 20;
        } else {
          r = 45; g = 130; b = 30;
        }

        const cx = textureSize / 2;
        const dx = Math.abs(x - cx);
        const distCenter = Math.sqrt(dx * dx + Math.abs(y - cx) ** 2);
        let wear = 0;
        if (distCenter < textureSize * 0.18) {
          wear = Math.max(0, 1 - distCenter / (textureSize * 0.18));
        }
        const distTop = y;
        const distBot = textureSize - y;
        if (distTop < textureSize * 0.1 && dx < textureSize * 0.15) {
          wear = Math.max(wear, 1 - Math.sqrt(dx * dx + distTop * distTop * 3) / (textureSize * 0.15));
        }
        if (distBot < textureSize * 0.1 && dx < textureSize * 0.15) {
          wear = Math.max(wear, 1 - Math.sqrt(dx * dx + distBot * distBot * 3) / (textureSize * 0.15));
        }
        wear *= (0.3 + Math.random() * 0.7);

        if (wear > 0.05) {
          const w = Math.min(1, wear * 1.5);
          r = r * (1 - w) + (90 + Math.random() * 20) * w;
          g = g * (1 - w) + (80 + Math.random() * 15) * w;
          b = b * (1 - w) + (50 + Math.random() * 10) * w;
        }

        const noise = (Math.random() - 0.5) * 35;
        r = Math.max(0, Math.min(255, r + noise));
        g = Math.max(0, Math.min(255, g + noise));
        b = Math.max(0, Math.min(255, b + noise));

        ctxAlb.fillStyle = `rgb(${Math.floor(r)},${Math.floor(g)},${Math.floor(b)})`;
        ctxAlb.fillRect(x, y, 1, 1);
      }
    }

    albedoTexture.update();
    grassMat.diffuseTexture = albedoTexture;
    grassMat.specularColor = new BABYLON.Color3(0.15, 0.15, 0.1);
    grassMat.specularPower = 16;

    return grassMat;
  }

  /**
   * Create stadium floodlights
   */
  private static _createStadiumLights(scene: BABYLON.Scene): void {
    const lightPositions = [
      { x: -15, z: -25, intensity: 150 },
      { x: 15, z: -25, intensity: 150 },
    ];

    lightPositions.forEach((pos, i) => {
      const light = new BABYLON.PointLight(
        `stadiumLight${i}`,
        new BABYLON.Vector3(pos.x, 25, pos.z),
        scene
      );
      light.intensity = pos.intensity;
      light.diffuse = new BABYLON.Color3(1, 0.98, 0.95);
      light.specular = new BABYLON.Color3(1, 1, 1);
      light.range = 80;

      // Add visible light fixture
      const fixture = BABYLON.MeshBuilder.CreateCylinder(
        `lightFixture${i}`,
        { height: 1.5, diameter: 1.2 },
        scene
      );
      fixture.position = new BABYLON.Vector3(pos.x, 26, pos.z);
      const fixtureMat = new BABYLON.StandardMaterial(`fixtureMat${i}`, scene);
      fixtureMat.diffuseColor = new BABYLON.Color3(0.2, 0.2, 0.2);
      fixtureMat.emissiveColor = new BABYLON.Color3(0.4, 0.4, 0.35);
      fixture.material = fixtureMat;
      fixture.freezeWorldMatrix();
    });
  }

  /**
   * Ultra realistic post-processing pipeline
   */
  private static _setupUltraRealisticPostProcessing(
    scene: BABYLON.Scene,
    camera: BABYLON.Camera,
    engine: BABYLON.Engine
  ): void {
    // Default rendering pipeline for cinematic quality
    const pipeline = new BABYLON.DefaultRenderingPipeline(
      "ultraRealisticPipeline",
      true,
      scene,
      [camera]
    );

    // --- ANTI-ALIASING ---
    pipeline.fxaaEnabled = true;
    pipeline.samples = 4; // MSAA

    // --- BLOOM ---
    pipeline.bloomEnabled = true;
    pipeline.bloomThreshold = 0.98;
    pipeline.bloomWeight = 0.05;
    pipeline.bloomKernel = 8;
    pipeline.bloomScale = 0.2;

    // --- DEPTH OF FIELD (disabled — too blurry for gameplay) ---
    pipeline.depthOfFieldEnabled = false;

    // --- CHROMATIC ABERRATION ---
    pipeline.chromaticAberrationEnabled = false;

    // --- GRAIN ---
    pipeline.grainEnabled = false;

    // --- SHARPEN ---
    pipeline.sharpenEnabled = true;
    pipeline.sharpen.edgeAmount = 0.1;

    // --- TONE MAPPING & COLOR GRADING ---
    pipeline.imageProcessingEnabled = true;
    pipeline.imageProcessing.contrast = 1.1;
    pipeline.imageProcessing.exposure = 0.85;
    pipeline.imageProcessing.toneMappingEnabled = true;
    pipeline.imageProcessing.toneMappingType = BABYLON.ImageProcessingConfiguration.TONEMAPPING_ACES;
    pipeline.imageProcessing.vignetteEnabled = false;

    // --- GLOW LAYER ---
    const gl = new BABYLON.GlowLayer("glow", scene, {
      mainTextureFixedSize: 256,
      blurKernelSize: 8
    });
    gl.intensity = 0.05;
  }

  private static _ballPos: BABYLON.Vector3 = BABYLON.Vector3.Zero();
  private static _lastCamRotation = 0;
  public static _camRotationSpeed = 0;

  // Crowd instances and banners handled by RoundedStadium now

  public static setCrowdVisible(visible: boolean): void {
    RoundedStadium.setCrowdVisible(visible);
  }

  public static update(dt: number, ballPos: BABYLON.Vector3, camRotation: number): void {
    SceneManager._ballPos.copyFrom(ballPos);

    // Track camera rotation speed
    let diff = camRotation - SceneManager._lastCamRotation;
    while (diff > 180) diff -= 360;
    while (diff < -180) diff += 360;
    SceneManager._camRotationSpeed = Math.abs(diff) / Math.max(dt, 0.001);
    SceneManager._lastCamRotation = camRotation;

    RoundedStadium.update(dt, SceneManager._camRotationSpeed);
  }

  public static triggerGoalCelebration(): void {
    RoundedStadium.triggerGoalCelebration();
  }

  private static _createFieldLines(scene: BABYLON.Scene): void {
    // High-visibility emissive material for field lines with maximum shininess
    const lineMat = new BABYLON.StandardMaterial("lineMat", scene);
    lineMat.emissiveColor = new BABYLON.Color3(0.6, 0.6, 0.6);
    lineMat.diffuseColor = new BABYLON.Color3(1, 1, 1);
    lineMat.specularColor = new BABYLON.Color3(1, 1, 1);
    lineMat.specularPower = 64; // High power for sharp highlights
    lineMat.disableLighting = false;
    lineMat.backFaceCulling = false;
    lineMat.zOffset = -5;

    const lineY = 0.01;

    // Halfway line
    const halfwayLine = BABYLON.MeshBuilder.CreatePlane(
      "halfway",
      { width: FIELD_WIDTH - 0.2, height: 0.18 },
      scene
    );
    halfwayLine.rotation.x = Math.PI / 2;
    halfwayLine.position.y = lineY;
    halfwayLine.material = lineMat;
    halfwayLine.freezeWorldMatrix();

    // Center circle (Matched thickness to 0.15m)
    const circle = BABYLON.MeshBuilder.CreateTorus(
      "centerCircle",
      { diameter: 10, thickness: 0.15, tessellation: 64 },
      scene
    );
    circle.position.y = lineY;
    circle.rotation = BABYLON.Vector3.Zero();
    circle.scaling.y = 0.01;
    circle.material = lineMat;
    circle.freezeWorldMatrix();

    // Center spot
    const spot = BABYLON.MeshBuilder.CreateDisc(
      "centerSpot",
      { radius: 0.25, tessellation: 32 },
      scene
    );
    spot.rotation.x = Math.PI / 2;
    spot.position.y = lineY;
    spot.material = lineMat;
    spot.freezeWorldMatrix();

    // --- Outer Boundaries ---
    for (const side of [-1, 1]) {
      const touchline = BABYLON.MeshBuilder.CreatePlane(
        `touchline_${side}`,
        { width: 0.18, height: FIELD_LENGTH },
        scene
      );
      touchline.rotation.x = Math.PI / 2;
      touchline.position.set(side * (FIELD_WIDTH / 2), lineY, 0);
      touchline.material = lineMat;
      touchline.freezeWorldMatrix();
    }

    for (const end of [-1, 1]) {
      const goalLine = BABYLON.MeshBuilder.CreatePlane(
        `goalLine_${end}`,
        { width: FIELD_WIDTH, height: 0.18 },
        scene
      );
      goalLine.rotation.x = Math.PI / 2;
      goalLine.position.set(0, lineY, end * (FIELD_LENGTH / 2));
      goalLine.material = lineMat;
      goalLine.freezeWorldMatrix();
    }

    SceneManager._createPenaltyAreas(scene, lineMat, lineY);
    SceneManager._createCornerArcs(scene, lineMat, lineY);
  }

  private static _createPenaltyAreas(scene: BABYLON.Scene, lineMat: BABYLON.Material, lineY: number): void {
    const penaltyWidth = 16;
    const penaltyDepth = 6;
    const goalAreaWidth = 8;
    const goalAreaDepth = 2.5;
    const goalZ = FIELD_LENGTH / 2;

    for (const end of [-1, 1]) {
      const zBase = end * goalZ;
      const pz = zBase - (end * penaltyDepth / 2);

      for (const side of [-1, 1]) {
        const sideLine = BABYLON.MeshBuilder.CreatePlane(`penSide_${end}_${side}`, { width: 0.15, height: penaltyDepth }, scene);
        sideLine.rotation.x = Math.PI / 2;
        sideLine.position.set(side * penaltyWidth / 2, lineY, pz);
        sideLine.material = lineMat;
        sideLine.freezeWorldMatrix();
      }

      const frontLine = BABYLON.MeshBuilder.CreatePlane(`penFront_${end}`, { width: penaltyWidth, height: 0.15 }, scene);
      frontLine.rotation.x = Math.PI / 2;
      frontLine.position.set(0, lineY, zBase - (end * penaltyDepth));
      frontLine.material = lineMat;
      frontLine.freezeWorldMatrix();

      const gaz = zBase - (end * goalAreaDepth / 2);
      for (const side of [-1, 1]) {
        const gaSide = BABYLON.MeshBuilder.CreatePlane(`gaSide_${end}_${side}`, { width: 0.15, height: goalAreaDepth }, scene);
        gaSide.rotation.x = Math.PI / 2;
        gaSide.position.set(side * goalAreaWidth / 2, lineY, gaz);
        gaSide.material = lineMat;
        gaSide.freezeWorldMatrix();
      }
      const gaFront = BABYLON.MeshBuilder.CreatePlane(`gaFront_${end}`, { width: goalAreaWidth, height: 0.15 }, scene);
      gaFront.rotation.x = Math.PI / 2;
      gaFront.position.set(0, lineY, zBase - (end * goalAreaDepth));
      gaFront.material = lineMat;
      gaFront.freezeWorldMatrix();

      const spot = BABYLON.MeshBuilder.CreateDisc(`penSpot_${end}`, { radius: 0.2, tessellation: 32 }, scene);
      spot.rotation.x = Math.PI / 2;
      spot.position.set(0, lineY, zBase - (end * 4.5));
      spot.material = lineMat;
      spot.freezeWorldMatrix();

      const arc = BABYLON.MeshBuilder.CreateTorus(`penArc_${end}`, {
        diameter: 6,
        thickness: 0.15, // Matched thickness
        tessellation: 32
      }, scene);
      arc.position.set(0, lineY, zBase - (end * penaltyDepth));
      arc.rotation = BABYLON.Vector3.Zero();
      arc.scaling.y = 0.01;
      arc.material = lineMat;
      arc.freezeWorldMatrix();
    }
  }

  private static _createCornerArcs(scene: BABYLON.Scene, lineMat: BABYLON.Material, lineY: number): void {
    const arcRadius = 1;
    for (const x of [-1, 1]) {
      for (const z of [-1, 1]) {
        const arc = BABYLON.MeshBuilder.CreateTorus(`cornerArc${x}${z}`, {
          diameter: arcRadius * 2,
          thickness: 0.15, // Matched thickness
          tessellation: 32
        }, scene);
        arc.position.set(x * (FIELD_WIDTH / 2), lineY, z * (FIELD_LENGTH / 2));
        arc.rotation = BABYLON.Vector3.Zero();
        arc.scaling.y = 0.01;
        arc.material = lineMat;
        arc.freezeWorldMatrix();
      }
    }
  }

  private static _createWalls(scene: BABYLON.Scene): void {
    // Sleek Modern Wall Material
    const wallMat = new BABYLON.StandardMaterial("wallMat", scene);
    wallMat.diffuseColor = new BABYLON.Color3(0.95, 0.95, 0.98);
    wallMat.specularColor = new BABYLON.Color3(0.4, 0.4, 0.4);

    const trimMat = new BABYLON.StandardMaterial("trimMat", scene);
    trimMat.diffuseColor = new BABYLON.Color3(0.85, 0.08, 0.12);
    trimMat.specularColor = new BABYLON.Color3(0.2, 0.2, 0.2);

    // Long sides (along Z)
    for (const side of [-1, 1]) {
      const wall = BABYLON.MeshBuilder.CreateBox(
        `wallSide${side}`,
        {
          width: WALL_THICKNESS,
          height: WALL_HEIGHT,
          depth: FIELD_LENGTH + WALL_THICKNESS * 2,
        },
        scene
      );
      wall.position = new BABYLON.Vector3(
        side * (FIELD_WIDTH / 2 + WALL_THICKNESS / 2),
        WALL_HEIGHT / 2,
        0
      );
      wall.material = wallMat;
      wall.freezeWorldMatrix();

      // Add colored top trim
      const trim = BABYLON.MeshBuilder.CreateBox(`trimSide${side}`, { width: WALL_THICKNESS + 0.1, height: 0.2, depth: FIELD_LENGTH + WALL_THICKNESS * 2 + 0.1 }, scene);
      trim.position = new BABYLON.Vector3(wall.position.x, WALL_HEIGHT, wall.position.z);
      trim.material = trimMat;
      trim.freezeWorldMatrix();
    }

    // Short sides (along X), but with gaps for goals (goal width ~7)
    const goalGap = 7;
    for (const end of [-1, 1]) {
      const z = end * (FIELD_LENGTH / 2 + WALL_THICKNESS / 2);
      const sideWidth = (FIELD_WIDTH - goalGap) / 2;

      for (const lr of [-1, 1]) {
        const wall = BABYLON.MeshBuilder.CreateBox(
          `wallEnd${end}_${lr}`,
          {
            width: sideWidth,
            height: WALL_HEIGHT,
            depth: WALL_THICKNESS,
          },
          scene
        );
        wall.position = new BABYLON.Vector3(
          lr * ((goalGap / 2) + sideWidth / 2),
          WALL_HEIGHT / 2,
          z
        );
        wall.material = wallMat;
        wall.freezeWorldMatrix();

        // Add colored top trim
        const trim = BABYLON.MeshBuilder.CreateBox(`trimEnd${end}_${lr}`, { width: sideWidth + 0.1, height: 0.2, depth: WALL_THICKNESS + 0.1 }, scene);
        trim.position = new BABYLON.Vector3(wall.position.x, WALL_HEIGHT, wall.position.z);
        trim.material = trimMat;
        trim.freezeWorldMatrix();
      }
    }
  }

  /** Add physics impostors to all walls - call AFTER physics is enabled */
  static addWallPhysics(scene: BABYLON.Scene): void {
    const walls: BABYLON.Mesh[] = [];

    // Collect all wall meshes (excluding goal area - goals are open!)
    for (const side of [-1, 1]) {
      const wall = scene.getMeshByName(`wallSide${side}`) as BABYLON.Mesh;
      if (wall) walls.push(wall);
    }

    const goalGap = 7;
    for (const end of [-1, 1]) {
      for (const lr of [-1, 1]) {
        const wall = scene.getMeshByName(`wallEnd${end}_${lr}`) as BABYLON.Mesh;
        if (wall) walls.push(wall);
      }
      // NO back wall - goals are open so balls can go in!
    }

    // Add physics to all walls
    for (const wall of walls) {
      if (!wall.physicsImpostor) {
        wall.physicsImpostor = new BABYLON.PhysicsImpostor(
          wall,
          BABYLON.PhysicsImpostor.BoxImpostor,
          { mass: 0, restitution: 0.7, friction: 0.4 },
          scene
        );
      }
    }

    console.log(`Added physics to ${walls.length} walls (goals are open!)`);
  }

  private static _createSkybox(scene: BABYLON.Scene): void {
    const skybox = BABYLON.MeshBuilder.CreateBox(
      "skybox",
      { size: 1000 },
      scene
    );

    // Create a gradient sky material using shader
    const skyMat = new BABYLON.StandardMaterial("skyMat", scene);
    skyMat.backFaceCulling = false;
    skyMat.disableLighting = true;

    // Create a dynamic gradient texture
    const gradientTexture = new BABYLON.DynamicTexture("skyGradient", { width: 512, height: 512 }, scene);
    const ctx = gradientTexture.getContext();

    // Draw sky gradient
    const gradient = ctx.createLinearGradient(0, 0, 0, 512);
    gradient.addColorStop(0, "#4A90E2"); // Deep blue at top
    gradient.addColorStop(0.5, "#87CEEB"); // Sky blue
    gradient.addColorStop(1, "#E0F6FF"); // Light cyan at horizon

    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, 512, 512);
    gradientTexture.update();

    skyMat.diffuseTexture = gradientTexture;
    skyMat.specularColor = BABYLON.Color3.Black();
    skyMat.emissiveColor = new BABYLON.Color3(0.7, 0.85, 0.95);
    skybox.material = skyMat;
    skybox.infiniteDistance = true;
    skybox.freezeWorldMatrix();

    // Add some "clouds" using particle system or simple meshes
    SceneManager._createClouds(scene);
  }

  private static _createClouds(scene: BABYLON.Scene): void {
    // Create a few simple cloud meshes
    const cloudMat = new BABYLON.StandardMaterial("cloudMat", scene);
    cloudMat.diffuseColor = new BABYLON.Color3(1, 1, 1);
    cloudMat.alpha = 0.7;
    cloudMat.specularColor = BABYLON.Color3.Black();

    for (let i = 0; i < 5; i++) {
      const cloud = BABYLON.MeshBuilder.CreateSphere(`cloud${i}`, {
        diameter: 15 + Math.random() * 10,
        segments: 8
      }, scene);

      cloud.position = new BABYLON.Vector3(
        (Math.random() - 0.5) * 200,
        50 + Math.random() * 30,
        (Math.random() - 0.5) * 200
      );
      cloud.scaling = new BABYLON.Vector3(2, 0.6, 1);
      cloud.material = cloudMat;
      cloud.freezeWorldMatrix();
    }
  }
}